import { Injectable, Optional } from '@angular/core';
import { Branch } from '@services/environment/environment.service';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import merge from 'lodash/merge';
import set from 'lodash/set';
import unset from 'lodash/unset';
import { BehaviorSubject } from 'rxjs';
import { EnvironmentService } from 'src/app/shared/services/environment/environment.service';
import { environment } from 'src/environments/environment';

/**
 * AlbertaDebugConfigService
 *
 * Provides a developer-only mechanism to override parts of the tenant configuration at runtime
 * without changing backend data. Overrides are stored in localStorage and are applied as a deep
 * patch (lodash.merge) on top of the base configuration. This service is ONLY active when the app
 * runs on the dev branch (environment.branch === Branch.dev). In any other branch, all methods are
 * no-ops and the console API is not registered.
 *
 * Console API (available on dev branch only):
 *   - AlbertaDebug.config.set(path: string, value: any): set a single path override
 *   - AlbertaDebug.config.setJson(objOrJson: object|string): deep-merge an object/JSON string
 *   - AlbertaDebug.config.patch(objOrJson: object|string): alias for setJson
 *   - AlbertaDebug.config.get(path: string): read current override value at path
 *   - AlbertaDebug.config.list(): returns a snapshot of all current overrides
 *   - AlbertaDebug.config.remove(path: string): remove a specific override path
 *   - AlbertaDebug.config.clear(): remove all overrides
 *
 * Usage examples (in browser dev tools console):
 *   // Set a single flag
 *   AlbertaDebug.config.set('orderConfig.showSendOrderDialog', false);
 *
 *   // Patch multiple keys at once (object)
 *   AlbertaDebug.config.setJson({ kiwiConfig: { enabled: true }, orderConfig: { enableDrafts: true } });
 *
 *   // Patch via JSON string
 *   AlbertaDebug.config.patch('{"patientData": {"showDeliveryInformation": true}}');
 *
 *   // Inspect and clear overrides
 *   AlbertaDebug.config.list();
 *   AlbertaDebug.config.clear();
 *
 * UI/Console behavior:
 *   - When overrides are active, a red console banner is printed.
 *   - The app shell shows a small red badge "DEBUG: Config Overrides Active" with a Clear link.
 *   - On clear, a green console message confirms that all overrides were removed.
 */
@Injectable({ providedIn: 'root' })
export class AlbertaDebugConfigService {
  private readonly OVERRIDES_STORAGE_KEY = 'alberta_debug_tenantConfigOverrides';
  private _overrides: Record<string, any> = {};
  private _bannerPrinted = false;
  private _hasOverrides$ = new BehaviorSubject<boolean>(false);

  constructor(@Optional() private _envService?: EnvironmentService) {
    this.init();
  }

  private async init() {
    await this._envService?.ready;
    this._loadOverridesFromStorage();
    this._registerConsoleApi();
    this._printDebugBannerIfOverrides();
  }

  // Observable to allow UI to react on changes
  public get hasOverrides$() {
    return this._hasOverrides$.asObservable();
  }
  public get hasOverrides(): boolean {
    return this._hasOverrides$.getValue();
  }

  // Public API for consumers
  applyOverrides<T>(base: T | null | undefined): T | null {
    if (!base) return null;
    const merged = cloneDeep(base) as any;
    if (this.isDevEnvironment()) {
      // Deep patch semantics: only the provided keys are overridden, others remain intact
      merge(merged, this._overrides || {});
    }
    return merged as T;
  }

  // Console API backing methods
  set(path: string, value: any): any {
    if (!this.isDevEnvironment() || !path) return undefined;
    set(this._overrides as any, path, value);
    this._saveOverridesToStorage();
    this._emitHasOverrides();
    this._printDebugBannerIfOverrides();
    return get(this.applyOverrides({}) as any, path); // return something consistent
  }

  get(path: string): any {
    return get(this._overrides as any, path);
  }

  remove(path: string): void {
    if (!this.isDevEnvironment() || !path) return;
    unset(this._overrides as any, path);
    if (Object.keys(this._overrides || {}).length === 0) {
      this._overrides = {};
    }
    this._saveOverridesToStorage();
    this._emitHasOverrides();
    this._printDebugBannerIfOverrides();
  }

  list(): Record<string, any> {
    return cloneDeep(this._overrides || {});
  }

  /**
   * Accept a JSON object (or JSON string) and deep-merge it into the overrides.
   * This performs a patch: only provided keys are overridden.
   */
  setJson(obj: unknown): void {
    if (!this.isDevEnvironment()) return;
    try {
      const payload = typeof obj === 'string' ? JSON.parse(obj) : obj;
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        merge(this._overrides as any, payload as Record<string, any>);
        this._saveOverridesToStorage();
        this._emitHasOverrides();
        this._printDebugBannerIfOverrides();
      } else {
        // eslint-disable-next-line no-console
        console.warn('AlbertaDebug.config.setJson expects a JSON object or JSON string of an object.');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('AlbertaDebug.config.setJson failed to parse JSON:', e);
    }
  }

  clear(): void {
    if (!this.isDevEnvironment()) return;
    this._overrides = {};
    this._saveOverridesToStorage();
    this._emitHasOverrides();
    this._printDebugBannerIfOverrides();
  }

  // Helpers
  private _loadOverridesFromStorage(): void {
    try {
      const localStorage =
        typeof window !== 'undefined' && typeof window.localStorage !== 'undefined' ? window.localStorage : null;
      const rawOverridesFromStorage = localStorage?.getItem(this.OVERRIDES_STORAGE_KEY);
      this._overrides = rawOverridesFromStorage ? JSON.parse(rawOverridesFromStorage) : {};
    } catch {
      this._overrides = {};
    }
    this._emitHasOverrides();
  }

  private _emitHasOverrides(): void {
    const has = this.isDevEnvironment() && this._overrides && Object.keys(this._overrides).length > 0;
    this._hasOverrides$.next(Boolean(has));
  }

  private _saveOverridesToStorage(): void {
    try {
      const localStorage =
        typeof window !== 'undefined' && typeof window.localStorage !== 'undefined' ? window.localStorage : null;
      localStorage?.setItem(this.OVERRIDES_STORAGE_KEY, JSON.stringify(this._overrides || {}));
    } catch {
      // ignore storage errors in dev
    }
  }

  private _printDebugBannerIfOverrides(): void {
    if (!this.isDevEnvironment()) return;
    const hasOverrides = this._overrides && Object.keys(this._overrides).length > 0;
    if (hasOverrides) {
      const msg = '🐞 ALBERTA DEBUG: LOCAL TENANT CONFIG OVERRIDES ACTIVE';
      const style = 'background:#c62828;color:#fff;font-weight:700;font-size:12px;padding:8px 12px;border-radius:4px;';
      // eslint-disable-next-line no-console
      console.log('%c' + msg, style);
      // eslint-disable-next-line no-console
      console.log(
        '%cUse AlbertaDebug.config.list() to inspect overrides. Use AlbertaDebug.config.clear() to clear overrides.',
        'color:#c62828;font-weight:600;'
      );
      this._bannerPrinted = true;
    } else if (this._bannerPrinted) {
      // eslint-disable-next-line no-console
      console.log(
        '%c🪲 ALBERTA DEBUG: All local tenant config overrides cleared.',
        'background:#2e7d32;color:#fff;font-weight:700;font-size:12px;padding:6px 10px;border-radius:4px;'
      );
      this._bannerPrinted = false;
    }
  }

  private _registerConsoleApi(): void {
    if (!this.isDevEnvironment()) return;
    const globalObj = window as any;
    globalObj.AlbertaDebug = globalObj.AlbertaDebug || {};
    const self = this;
    globalObj.AlbertaDebug.config = {
      set(path: string, value: any) {
        return self.set(path, value);
      },
      setJson(payload: any) {
        return self.setJson(payload);
      },
      patch(payload: any) {
        return self.setJson(payload);
      },
      get(path: string) {
        return self.get(path);
      },
      remove(path: string) {
        return self.remove(path);
      },
      list() {
        return self.list();
      },
      clear() {
        return self.clear();
      },
      dump() {
        // Return a snapshot of current overrides
        return cloneDeep(self.list());
      },
    };
  }

  // Determine if debug overrides are enabled based on branch (dev only)
  public isDevEnvironment(): boolean {
    const branch = this._envService?.branch ?? environment.branch;
    return branch === Branch.dev;
  }
}
